/**
 * MassRVGuy May Sales War Board
 * Live accountability scoreboard for RV sales goals
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
  Target, TrendingUp, Calendar, Clock, Plus, Minus, Check, 
  Trash2, DollarSign, Flame, Zap, AlertTriangle, Trophy,
  RefreshCw, X, ChevronUp, Upload, Sparkles, Loader2, Archive
} from 'lucide-react';
import { getAuthToken } from '../../utils/auth';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Constants
const DEFAULT_MONTHLY_GOAL = 15;
// Target month/year auto-set to the current month so the War Board rolls over
// each new month (was previously hard-pinned to May 2026).
const _now = new Date();
const TARGET_MONTH = _now.getMonth(); // 0-indexed current month
const TARGET_YEAR = _now.getFullYear();
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const TARGET_MONTH_NAME = MONTH_NAMES[TARGET_MONTH];

const INITIAL_DEALS = [];

const DAILY_TASKS = [
  { id: 'calls', name: 'Prospecting Calls', target: 35, icon: '📞' },
  { id: 'texts', name: 'Texts', target: 25, icon: '💬' },
  { id: 'followups', name: 'Follow Ups', target: 20, icon: '🔄' },
  { id: 'leads', name: 'New Leads', target: 10, icon: '🎯' },
  { id: 'appointments', name: 'Appointments Set', target: 2, icon: '📅' },
  { id: 'shown', name: 'Appointment Shown', target: 1, icon: '👋' },
  { id: 'sold', name: 'Sold Today', target: 1, icon: '🏆' }
];

const MOTIVATIONAL_QUOTES = [
  "You are not trying to hit 15. You are executing a takeover.",
  "Every lead gets worked until it buys or dies.",
  "Speed beats hesitation.",
  "Follow-up is where deals are stolen.",
  "Today's job: create one more unit."
];

const SalesWarBoard = () => {
  // State
  const [currentUnits, setCurrentUnits] = useState(4);
  const [monthlyGoal, setMonthlyGoal] = useState(DEFAULT_MONTHLY_GOAL);
  const [showGoalEditor, setShowGoalEditor] = useState(false);
  const [goalDraft, setGoalDraft] = useState(String(DEFAULT_MONTHLY_GOAL));
  const [deals, setDeals] = useState(INITIAL_DEALS);
  const [dailyProgress, setDailyProgress] = useState({});
  const [monthlyProgress, setMonthlyProgress] = useState({});
  const [monthlyReconciliations, setMonthlyReconciliations] = useState([]);
  const [lastResetDate, setLastResetDate] = useState('');
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [newDeal, setNewDeal] = useState({ name: '', unit: '', status: 'Pending Finance', date: '', notes: '' });
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [countdown, setCountdown] = useState({ midnight: {}, endOfMonth: {} });
  const [currentQuote, setCurrentQuote] = useState(0);

  // CRM reconciliation diff modal
  const [reconcilePreview, setReconcilePreview] = useState(null); // { proposed: {field: newVal, ...}, source_label, image_count }
  const [reconcileSubmitting, setReconcileSubmitting] = useState(false);

  // Backend hydration + history
  const [hydrated, setHydrated] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState({ monthly: [], daily: [] });
  const [historyLoading, setHistoryLoading] = useState(false);
  const saveTimerRef = useRef(null);

  // Calculate days left in May
  const getDaysLeftInMay = useCallback(() => {
    const now = new Date();
    const endOfMay = new Date(TARGET_YEAR, TARGET_MONTH + 1, 0, 23, 59, 59);
    const diffTime = endOfMay - now;
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }, []);

  // Calculate metrics
  const remaining = monthlyGoal - currentUnits;
  const daysLeft = getDaysLeftInMay();
  const paceNeeded = daysLeft > 0 ? (remaining / daysLeft).toFixed(2) : 0;
  const progressPercent = (currentUnits / (monthlyGoal || 1)) * 100;

  // Hydrate from backend on mount (falls back to localStorage cache)
  useEffect(() => {
    let active = true;
    const hydrate = async () => {
      const cached = localStorage.getItem('salesWarBoard');
      if (cached) {
        try {
          const data = JSON.parse(cached);
          setCurrentUnits(data.currentUnits ?? 4);
          setDeals(data.deals ?? INITIAL_DEALS);
          setDailyProgress(data.dailyProgress ?? {});
          setLastResetDate(data.lastResetDate ?? '');
          if (typeof data.monthlyGoal === 'number' && data.monthlyGoal > 0) {
            setMonthlyGoal(data.monthlyGoal);
            setGoalDraft(String(data.monthlyGoal));
          }
        } catch (_) { /* ignore */ }
      }
      try {
        const token = getAuthToken();
        if (!token) { setHydrated(true); return; }
        const res = await axios.get(`${API}/dealer/war-board/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active) return;
        const s = res.data || {};
        if (typeof s.current_units === 'number') setCurrentUnits(s.current_units);
        if (typeof s.monthly_goal === 'number' && s.monthly_goal > 0) {
          setMonthlyGoal(s.monthly_goal);
          setGoalDraft(String(s.monthly_goal));
        }
        if (Array.isArray(s.deals)) setDeals(s.deals.length ? s.deals : INITIAL_DEALS);
        if (s.daily_progress && typeof s.daily_progress === 'object') setDailyProgress(s.daily_progress);
        if (s.monthly_progress && typeof s.monthly_progress === 'object') setMonthlyProgress(s.monthly_progress);
        if (Array.isArray(s.monthly_reconciliations)) setMonthlyReconciliations(s.monthly_reconciliations);
        if (s.date_str_est) setLastResetDate(s.date_str_est);
      } catch (_) {
        // backend offline or auth missing — localStorage already restored
      } finally {
        if (active) setHydrated(true);
      }
    };
    hydrate();
    return () => { active = false; };
  }, []);

  // Save to localStorage immediately (cache) + debounced PUT to backend
  useEffect(() => {
    const data = { currentUnits, deals, dailyProgress, lastResetDate, monthlyGoal };
    localStorage.setItem('salesWarBoard', JSON.stringify(data));
    if (!hydrated) return; // skip backend save until first hydration completes
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await axios.put(`${API}/dealer/war-board/state`,
          { current_units: currentUnits, deals, daily_progress: dailyProgress, monthly_goal: monthlyGoal },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // If backend rolled over (e.g., date crossed midnight EST), apply server state
        const serverState = res.data?.state;
        if (serverState && serverState.date_str_est && serverState.date_str_est !== lastResetDate) {
          setLastResetDate(serverState.date_str_est);
          setDailyProgress(serverState.daily_progress || {});
          if (typeof serverState.current_units === 'number') setCurrentUnits(serverState.current_units);
          if (Array.isArray(serverState.deals)) setDeals(serverState.deals.length ? serverState.deals : INITIAL_DEALS);
        }
      } catch (_) { /* silent — cached locally */ }
    }, 1200);
  }, [currentUnits, deals, dailyProgress, lastResetDate, hydrated]);

  // Auto-sync currentUnits from backend sales data for TARGET_MONTH/TARGET_YEAR
  // Falls back to localStorage value if fetch fails or no sales recorded yet
  useEffect(() => {
    const syncUnitsFromBackend = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await axios.get(`${API}/dealer/sales/analytics/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const targetKey = `${TARGET_YEAR}-${String(TARGET_MONTH + 1).padStart(2, '0')}`;
        const monthData = (res.data?.monthly_data || []).find(d => d.month === targetKey);
        if (monthData && typeof monthData.units === 'number') {
          setCurrentUnits(monthData.units);
        }
      } catch (err) {
        // Silent fail — keep localStorage value
      }
    };
    syncUnitsFromBackend();
    // Re-sync when user returns to this tab
    const onFocus = () => syncUnitsFromBackend();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Server handles daily reset at 11:59 PM EST — re-fetch on focus to pick up
  // any rollover the backend applied while this tab was inactive
  useEffect(() => {
    const onFocus = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await axios.get(`${API}/dealer/war-board/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const s = res.data || {};
        if (s.date_str_est && s.date_str_est !== lastResetDate) {
          setLastResetDate(s.date_str_est);
          setDailyProgress(s.daily_progress || {});
          if (s.monthly_progress && typeof s.monthly_progress === 'object') setMonthlyProgress(s.monthly_progress);
          if (Array.isArray(s.monthly_reconciliations)) setMonthlyReconciliations(s.monthly_reconciliations);
          if (typeof s.current_units === 'number') setCurrentUnits(s.current_units);
          if (Array.isArray(s.deals)) setDeals(s.deals.length ? s.deals : INITIAL_DEALS);
          toast.message('War Board synced — daily counters reset for the new day.');
        }
      } catch (_) { /* silent */ }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [lastResetDate]);

  // Countdown timer — uses EST (America/New_York) to match server reset boundary
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();

      // Format helper: get parts of "now" in EST
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
      const parts = Object.fromEntries(fmt.formatToParts(now).map(p => [p.type, p.value]));
      const estYear = parseInt(parts.year, 10);
      const estMonth = parseInt(parts.month, 10); // 1-12
      const estDay = parseInt(parts.day, 10);
      const estHour = parseInt(parts.hour, 10);
      const estMin = parseInt(parts.minute, 10);
      const estSec = parseInt(parts.second, 10);

      // Seconds until 11:59:00 PM EST today (or tomorrow if already past)
      let secsToReset = (23 * 3600 + 59 * 60) - (estHour * 3600 + estMin * 60 + estSec);
      if (secsToReset < 0) secsToReset += 24 * 3600;
      const midnightHours = Math.floor(secsToReset / 3600);
      const midnightMinutes = Math.floor((secsToReset % 3600) / 60);
      const midnightSeconds = secsToReset % 60;

      // Last day of current EST month
      const lastDayOfMonth = new Date(Date.UTC(estYear, estMonth, 0)).getUTCDate();
      // Seconds from now (EST) to 11:59:59 PM EST on last day of month
      const daysLeftInMonth = lastDayOfMonth - estDay;
      const secsToEndOfDay = (23 * 3600 + 59 * 60 + 59) - (estHour * 3600 + estMin * 60 + estSec);
      const totalMonthSecs = Math.max(0, daysLeftInMonth * 86400 + secsToEndOfDay);

      const mayDays = Math.floor(totalMonthSecs / 86400);
      const mayHours = Math.floor((totalMonthSecs % 86400) / 3600);
      const mayMinutes = Math.floor((totalMonthSecs % 3600) / 60);
      const maySeconds = totalMonthSecs % 60;

      setCountdown({
        midnight: { hours: midnightHours, minutes: midnightMinutes, seconds: midnightSeconds },
        endOfMonth: { days: mayDays, hours: mayHours, minutes: mayMinutes, seconds: maySeconds }
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Rotate quotes
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote(prev => (prev + 1) % MOTIVATIONAL_QUOTES.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Handlers
  const updateTaskProgress = (taskId, delta) => {
    setDailyProgress(prev => {
      const current = prev[taskId] || 0;
      const newValue = Math.max(0, current + delta);
      return { ...prev, [taskId]: newValue };
    });
  };

  const addDeal = () => {
    if (!newDeal.name || !newDeal.unit) return;
    const deal = {
      ...newDeal,
      id: Date.now().toString(),
      sold: false
    };
    setDeals(prev => [...prev, deal]);
    setNewDeal({ name: '', unit: '', status: 'Pending Finance', date: '', notes: '' });
    setShowAddDeal(false);
  };

  const markDealSold = (dealId) => {
    setDeals(prev => prev.map(deal => {
      if (deal.id === dealId && !deal.sold) {
        setCurrentUnits(units => units + 1);
        return { ...deal, sold: true, status: 'SOLD' };
      }
      return deal;
    }));
  };

  const deleteDeal = (dealId) => {
    const deal = deals.find(d => d.id === dealId);
    if (deal?.sold) {
      setCurrentUnits(units => Math.max(0, units - 1));
    }
    setDeals(prev => prev.filter(d => d.id !== dealId));
  };

  const resetToday = () => {
    setDailyProgress({});
  };

  const resetMonth = () => {
    setCurrentUnits(0);
    setDeals(INITIAL_DEALS);
    setDailyProgress({});
    setShowResetConfirm(false);
  };

  const isTaskComplete = (taskId, target) => {
    return (dailyProgress[taskId] || 0) >= target;
  };

  const getTaskProgressPercent = (taskId, target) => {
    const current = dailyProgress[taskId] || 0;
    return Math.min(100, (current / target) * 100);
  };

  // Open history modal — fetches archived months from backend
  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/war-board/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data || { monthly: [], daily: [] });
    } catch (err) {
      toast.error('Could not load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // ---- AI image-analysis upload (sold log / comms / analytics) ----------
  const [uploadingImage, setUploadingImage] = useState(false);
  const [lastExtraction, setLastExtraction] = useState(null);

  const handleAnalyzeImage = async (files) => {
    const fileList = Array.isArray(files) ? files : (files ? [files] : []);
    if (!fileList.length) return;
    const validFiles = fileList.filter((f) => f && f.type && f.type.startsWith('image/'));
    if (!validFiles.length) {
      toast.error('Please upload PNG or JPG images only');
      return;
    }
    setUploadingImage(true);
    try {
      const token = getAuthToken();
      const fd = new FormData();
      validFiles.forEach((f) => fd.append('files', f));
      const res = await axios.post(`${API}/dealer/war-board/analyze-image`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.extracted;
      const processed = res.data?.images_processed || 0;
      if (!data) {
        toast.error("Couldn't read any stats from those images. Try clearer screenshots.");
        return;
      }

      const updates = [];
      if (typeof data.units_sold_month === 'number') {
        setCurrentUnits(data.units_sold_month);
        updates.push(`Units sold this month → ${data.units_sold_month}`);
      }

      // CRM reconciliation flow — extracted numbers REPLACE month-to-date totals
      // (CRM is source of truth, not additive). Show a diff confirm modal so the
      // dealer can reject bad OCR before we overwrite their MTD numbers.
      const proposedMonthly = {};
      if (data.daily_progress && typeof data.daily_progress === 'object') {
        for (const k of ['calls','texts','followups','leads','appointments','shown','sold']) {
          if (typeof data.daily_progress[k] === 'number' && data.daily_progress[k] >= 0) {
            proposedMonthly[k] = Math.round(data.daily_progress[k]);
          }
        }
      }
      if (typeof data.communication_count === 'number') {
        // Some CRM dashboards report a single "communications" total — treat as
        // calls+texts combined; we won't auto-split, but expose it as `communications`.
        proposedMonthly.communications = Math.round(data.communication_count);
      }

      if (Array.isArray(data.deals) && data.deals.length > 0) {
        const newDeals = data.deals
          .filter((d) => d && d.customer)
          .map((d, i) => ({
            id: `ai-${Date.now()}-${i}`,
            name: d.customer,
            unit: d.unit || '—',
            status: d.status || 'Pending',
            date: d.date || '',
            notes: 'AI imported',
            sold: (d.status || '').toLowerCase().includes('sold'),
          }));
        if (newDeals.length) {
          setDeals((prev) => [...prev, ...newDeals]);
          updates.push(`+${newDeals.length} deal${newDeals.length === 1 ? '' : 's'} added`);
        }
      }

      setLastExtraction(data);
      const imgLabel = processed === 1 ? '1 image' : `${processed} images`;

      // If we have proposed MTD reconciliation values, show the confirm modal.
      if (Object.keys(proposedMonthly).length > 0) {
        setReconcilePreview({
          proposed: proposedMonthly,
          source_label: 'CRM Upload',
          image_count: processed,
        });
        toast.message(`AI extracted ${Object.keys(proposedMonthly).length} MTD metric${Object.keys(proposedMonthly).length === 1 ? '' : 's'} — review before applying.`);
      } else if (updates.length) {
        toast.success(`AI processed ${imgLabel} → updated ${updates.length} item${updates.length === 1 ? '' : 's'}`);
      } else {
        toast.message(`AI processed ${imgLabel} — nothing matched a War Board stat.`);
      }
    } catch (err) {
      console.error('war-board upload failed', err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  // Apply (or cancel) the CRM-extracted MTD numbers after the dealer reviews
  // them in the diff modal. Send only the fields the dealer kept (they may
  // un-check rows in the modal — keptFields is the filtered map).
  const applyReconciliation = async (keptFields) => {
    if (!reconcilePreview || Object.keys(keptFields || {}).length === 0) {
      setReconcilePreview(null);
      return;
    }
    setReconcileSubmitting(true);
    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${API}/dealer/war-board/reconcile`,
        {
          monthly_progress: keptFields,
          source_label: reconcilePreview.source_label || 'CRM Upload',
          image_count: reconcilePreview.image_count || 0,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.monthly_progress) setMonthlyProgress(res.data.monthly_progress);
      // refresh full state to get the updated reconciliation audit log
      try {
        const stateRes = await axios.get(`${API}/dealer/war-board/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const s = stateRes.data || {};
        if (Array.isArray(s.monthly_reconciliations)) setMonthlyReconciliations(s.monthly_reconciliations);
      } catch (_) { /* ignore */ }
      const n = (res.data?.applied || []).length;
      toast.success(`MTD reconciled — ${n} field${n === 1 ? '' : 's'} updated from CRM.`);
      setReconcilePreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setReconcileSubmitting(false);
    }
  };

  // Helper — find the most-recent reconciliation entry for a given field this month
  const findReconcileMeta = (field) => {
    for (let i = monthlyReconciliations.length - 1; i >= 0; i--) {
      if (monthlyReconciliations[i]?.field === field) return monthlyReconciliations[i];
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
          <Flame className="w-7 h-7 sm:w-10 sm:h-10 text-red-500 animate-pulse" />
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-500 to-red-600">
            SALES WAR BOARD
          </h1>
          <Flame className="w-7 h-7 sm:w-10 sm:h-10 text-red-500 animate-pulse" />
        </div>
        <p className="text-base sm:text-xl md:text-2xl font-bold text-red-400 tracking-widest">
          MISSION: {monthlyGoal}+ UNITS
          <button
            onClick={() => { setGoalDraft(String(monthlyGoal)); setShowGoalEditor(true); }}
            className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 tracking-wider align-middle"
            data-testid="edit-monthly-goal-btn"
            title="Set this month's target"
          >
            Edit
          </button>
        </p>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">{TARGET_MONTH_NAME} {TARGET_YEAR}</p>
        <button
          onClick={openHistory}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800/80 hover:bg-gray-700 border border-gray-600 text-sm font-medium text-gray-200 transition-colors"
          data-testid="war-board-history-btn"
        >
          <Archive className="w-4 h-4" /> View Past Months
        </button>
      </div>

      {/* Main Progress Bar */}
      <div className="mb-8 bg-gray-800/50 rounded-2xl p-4 border border-gray-700">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-bold text-gray-400">MONTHLY PROGRESS</span>
          <span className="text-lg font-black text-white">{currentUnits} / {monthlyGoal}</span>
        </div>
        <div className="h-6 bg-gray-900 rounded-full overflow-hidden border border-gray-700">
          <div 
            className={`h-full transition-all duration-500 rounded-full ${
              progressPercent >= 100 
                ? 'bg-gradient-to-r from-green-500 to-emerald-400' 
                : progressPercent >= 75 
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-400'
                  : 'bg-gradient-to-r from-red-600 to-red-500'
            }`}
            style={{ width: `${Math.min(100, progressPercent)}%` }}
          />
        </div>
        <p className="text-center mt-2 text-sm text-gray-400">
          {progressPercent >= 100 ? '🏆 GOAL ACHIEVED!' : `${progressPercent.toFixed(1)}% to goal`}
        </p>
      </div>

      {/* AI Image Upload — drop multiple sold-log/comms/analytics screenshots, AI updates the board */}
      <div className="mb-8 rounded-2xl border-2 border-dashed border-amber-700/60 bg-gradient-to-br from-amber-950/40 to-gray-900/40 p-4 sm:p-5" data-testid="war-board-upload-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-amber-300 uppercase tracking-wider">AI War Board Importer</h3>
            <p className="text-sm text-gray-300 mt-1">
              Drop one or more screenshots — sold log, communications dashboard, analytics — AI reads them all and bumps every matching stat automatically.
            </p>
            {lastExtraction?.insights && (
              <p className="mt-2 text-xs italic text-amber-200/80" data-testid="war-board-upload-insight">
                Last analysis: {lastExtraction.insights}
                {typeof lastExtraction.confidence === 'number' && ` (${lastExtraction.confidence}% confidence)`}
              </p>
            )}
          </div>
          <label
            className={`shrink-0 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-3 rounded-xl font-bold text-sm cursor-pointer transition-all ${
              uploadingImage
                ? 'bg-gray-700 text-gray-400 cursor-wait'
                : 'bg-amber-500 hover:bg-amber-600 text-black shadow-lg hover:shadow-xl'
            }`}
            data-testid="war-board-upload-btn"
          >
            {uploadingImage ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Reading images…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" /> Upload screenshots
              </>
            )}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              disabled={uploadingImage}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) handleAnalyzeImage(files);
                e.target.value = '';
              }}
              data-testid="war-board-upload-input"
            />
          </label>
        </div>
      </div>

      {/* Scoreboard Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-8">
        <ScoreCard 
          label="CURRENT UNITS" 
          value={currentUnits} 
          icon={<Trophy className="w-6 h-6" />}
          color="text-green-400"
          bgColor="from-green-900/30 to-green-950/30"
          borderColor="border-green-700/50"
        />
        <ScoreCard 
          label="MONTHLY GOAL" 
          value={monthlyGoal}
          icon={<Target className="w-6 h-6" />}
          color="text-blue-400"
          bgColor="from-blue-900/30 to-blue-950/30"
          borderColor="border-blue-700/50"
        />
        <ScoreCard 
          label="REMAINING" 
          value={remaining} 
          icon={<AlertTriangle className="w-6 h-6" />}
          color="text-red-400"
          bgColor="from-red-900/30 to-red-950/30"
          borderColor="border-red-700/50"
          pulse={remaining > 0}
        />
        <ScoreCard 
          label="DAILY TARGET" 
          value={paceNeeded} 
          icon={<Zap className="w-6 h-6" />}
          color="text-yellow-400"
          bgColor="from-yellow-900/30 to-yellow-950/30"
          borderColor="border-yellow-700/50"
        />
        <ScoreCard 
          label="DAYS LEFT" 
          value={daysLeft} 
          icon={<Calendar className="w-6 h-6" />}
          color="text-purple-400"
          bgColor="from-purple-900/30 to-purple-950/30"
          borderColor="border-purple-700/50"
        />
        <ScoreCard 
          label="PACE NEEDED" 
          value={`${paceNeeded}/day`} 
          icon={<TrendingUp className="w-6 h-6" />}
          color="text-orange-400"
          bgColor="from-orange-900/30 to-orange-950/30"
          borderColor="border-orange-700/50"
          small
        />
      </div>

      {/* Countdown Timers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-5 border border-gray-700 shadow-xl">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-cyan-400" />
            <h3 className="text-sm font-bold text-gray-400 tracking-wider">DAILY RESET (11:59 PM EST)</h3>
          </div>
          <div className="flex justify-center gap-2">
            <CountdownUnit value={countdown.midnight.hours} label="HRS" />
            <span className="text-3xl font-bold text-gray-600 animate-pulse">:</span>
            <CountdownUnit value={countdown.midnight.minutes} label="MIN" />
            <span className="text-3xl font-bold text-gray-600 animate-pulse">:</span>
            <CountdownUnit value={countdown.midnight.seconds} label="SEC" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-900/30 to-gray-900/80 rounded-2xl p-5 border border-red-700/50 shadow-xl">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="w-5 h-5 text-red-400 animate-pulse" />
            <h3 className="text-sm font-bold text-gray-400 tracking-wider">END OF {TARGET_MONTH_NAME.toUpperCase()}</h3>
          </div>
          <div className="flex justify-center gap-2">
            <CountdownUnit value={countdown.endOfMonth.days} label="DAYS" highlight />
            <span className="text-3xl font-bold text-gray-600">:</span>
            <CountdownUnit value={countdown.endOfMonth.hours} label="HRS" />
            <span className="text-3xl font-bold text-gray-600">:</span>
            <CountdownUnit value={countdown.endOfMonth.minutes} label="MIN" />
            <span className="text-3xl font-bold text-gray-600">:</span>
            <CountdownUnit value={countdown.endOfMonth.seconds} label="SEC" />
          </div>
        </div>
      </div>

      {/* Daily Checklist */}
      <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 rounded-2xl p-5 border border-gray-700 shadow-xl mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Check className="w-6 h-6 text-green-400" />
            <h2 className="text-xl font-bold tracking-wider">DAILY ACCOUNTABILITY</h2>
          </div>
          <button
            onClick={resetToday}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Reset Today
          </button>
        </div>

        <div className="space-y-3">
          {DAILY_TASKS.map(task => {
            const progress = dailyProgress[task.id] || 0;
            const complete = isTaskComplete(task.id, task.target);
            const percent = getTaskProgressPercent(task.id, task.target);
            const mtd = monthlyProgress[task.id] || 0;
            const reconciled = findReconcileMeta(task.id);

            return (
              <div 
                key={task.id}
                className={`rounded-xl p-3 border transition-all ${
                  complete 
                    ? 'bg-green-900/30 border-green-600/50' 
                    : 'bg-gray-800/50 border-gray-700/50'
                }`}
                data-testid={`daily-task-${task.id}`}
              >
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap sm:flex-nowrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xl shrink-0">{task.icon}</span>
                    <span className={`font-semibold truncate ${complete ? 'text-green-400' : 'text-white'}`}>
                      {task.name}
                    </span>
                    {complete && <Check className="w-5 h-5 text-green-400 shrink-0" />}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => updateTaskProgress(task.id, -1)}
                      className="w-9 h-9 flex items-center justify-center bg-red-600/80 hover:bg-red-500 rounded-lg transition-colors"
                      data-testid={`daily-task-${task.id}-minus`}
                      aria-label={`Decrement ${task.name}`}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-16 text-center font-bold text-lg" data-testid={`daily-task-${task.id}-value`}>
                      {progress} / {task.target}
                    </span>
                    <button
                      onClick={() => updateTaskProgress(task.id, 1)}
                      className="w-9 h-9 flex items-center justify-center bg-green-600/80 hover:bg-green-500 rounded-lg transition-colors"
                      data-testid={`daily-task-${task.id}-plus`}
                      aria-label={`Increment ${task.name}`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="h-2 bg-gray-900 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-300 ${
                      complete ? 'bg-green-500' : 'bg-gradient-to-r from-red-500 to-orange-500'
                    }`}
                    style={{ width: `${percent}%` }}
                  />
                </div>
                {/* Month-to-date tally + CRM-reconciled badge */}
                <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
                  <span className="px-2 py-0.5 rounded-full bg-gray-900/80 border border-gray-700 text-gray-300 font-mono" data-testid={`mtd-${task.id}`}>
                    MTD: <span className="text-white font-bold">{mtd}</span>
                  </span>
                  {reconciled && (
                    <span
                      title={`Reconciled from ${reconciled.source_label} on ${reconciled.date_str_est} — was ${reconciled.previous_value}, set to ${reconciled.new_value}`}
                      className="px-2 py-0.5 rounded-full bg-amber-900/40 border border-amber-700/60 text-amber-200 inline-flex items-center gap-1"
                      data-testid={`mtd-${task.id}-reconciled`}
                    >
                      <Sparkles className="w-3 h-3" />
                      CRM • {reconciled.date_str_est?.slice(5)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Deal Board */}
      <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 rounded-2xl p-5 border border-gray-700 shadow-xl mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-400" />
            <h2 className="text-xl font-bold tracking-wider">DEAL BOARD</h2>
            <span className="px-2 py-0.5 bg-gray-700 rounded text-sm">{deals.length} deals</span>
          </div>
          <button
            onClick={() => setShowAddDeal(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-500 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Deal
          </button>
        </div>

        {/* Add Deal Form */}
        {showAddDeal && (
          <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-600">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">New Deal</h3>
              <button onClick={() => setShowAddDeal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Customer Name"
                value={newDeal.name}
                onChange={e => setNewDeal(prev => ({ ...prev, name: e.target.value }))}
                className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500"
              />
              <input
                type="text"
                placeholder="Unit (Year Make Model)"
                value={newDeal.unit}
                onChange={e => setNewDeal(prev => ({ ...prev, unit: e.target.value }))}
                className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500"
              />
              <input
                type="text"
                placeholder="Date"
                value={newDeal.date}
                onChange={e => setNewDeal(prev => ({ ...prev, date: e.target.value }))}
                className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500"
              />
              <input
                type="text"
                placeholder="Notes"
                value={newDeal.notes}
                onChange={e => setNewDeal(prev => ({ ...prev, notes: e.target.value }))}
                className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-green-500"
              />
            </div>
            <button
              onClick={addDeal}
              className="mt-3 w-full py-2 bg-green-600 hover:bg-green-500 rounded-lg font-bold transition-colors"
            >
              Add Deal
            </button>
          </div>
        )}

        {/* Deals List */}
        <div className="space-y-2">
          {deals.map(deal => (
            <div 
              key={deal.id}
              className={`rounded-xl p-3 border transition-all ${
                deal.sold 
                  ? 'bg-green-900/40 border-green-600/50' 
                  : 'bg-gray-800/50 border-gray-700/50 hover:border-gray-600'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-white">{deal.name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      deal.sold 
                        ? 'bg-green-600 text-white' 
                        : 'bg-yellow-600/80 text-white'
                    }`}>
                      {deal.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 truncate">{deal.unit}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>{deal.date}</span>
                    {deal.notes && <span>• {deal.notes}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!deal.sold && (
                    <button
                      onClick={() => markDealSold(deal.id)}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-500 rounded-lg text-sm font-bold transition-colors flex items-center gap-1"
                    >
                      <Trophy className="w-4 h-4" />
                      SOLD
                    </button>
                  )}
                  <button
                    onClick={() => deleteDeal(deal.id)}
                    className="p-1.5 bg-red-600/50 hover:bg-red-500 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {deals.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No deals yet. Add your first deal above!
            </div>
          )}
        </div>
      </div>

      {/* Motivation Panel */}
      <div className="bg-gradient-to-r from-red-900/40 via-gray-900/60 to-red-900/40 rounded-2xl p-6 border border-red-700/30 shadow-xl mb-8">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Flame className="w-6 h-6 text-red-500" />
          <h2 className="text-lg font-bold tracking-wider text-red-400">WAR ROOM MENTALITY</h2>
          <Flame className="w-6 h-6 text-red-500" />
        </div>
        <div className="relative h-16 overflow-hidden">
          <p 
            key={currentQuote}
            className="text-xl md:text-2xl font-bold text-center text-white italic animate-fadeIn"
          >
            "{MOTIVATIONAL_QUOTES[currentQuote]}"
          </p>
        </div>
        <div className="flex justify-center gap-1 mt-4">
          {MOTIVATIONAL_QUOTES.map((_, idx) => (
            <div 
              key={idx}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentQuote ? 'bg-red-500' : 'bg-gray-600'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Reset Controls */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          onClick={resetToday}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Reset Today's Progress
        </button>
        <button
          onClick={() => setShowResetConfirm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-600 rounded-lg font-medium transition-colors"
        >
          <AlertTriangle className="w-4 h-4" />
          Reset Entire Month
        </button>
      </div>

      {/* CRM Reconciliation Diff Modal — confirm before overwriting MTD */}
      {reconcilePreview && (
        <ReconcileDiffModal
          preview={reconcilePreview}
          monthlyProgress={monthlyProgress}
          submitting={reconcileSubmitting}
          onCancel={() => setReconcilePreview(null)}
          onApply={applyReconciliation}
          isDark={true}
        />
      )}

      {/* Past Months / Daily History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="war-board-history-modal">
          <div className="bg-gray-900 rounded-2xl p-5 sm:p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Archive className="w-6 h-6 text-amber-400" />
                <h3 className="text-xl font-bold">Past Months & Daily Logs</h3>
              </div>
              <button onClick={() => setShowHistory(false)} className="text-gray-400 hover:text-white" data-testid="war-board-history-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {historyLoading ? (
              <div className="text-center py-12 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-amber-300 uppercase tracking-wider mb-3">Monthly Stats</h4>
                  {history.monthly.length === 0 ? (
                    <p className="text-gray-500 text-sm italic">No archived months yet — your first month will appear here on the 1st of next month.</p>
                  ) : (
                    <div className="space-y-3">
                      {history.monthly.map((m) => (
                        <div key={m.month_str_est} className="bg-gray-800/60 rounded-xl p-4 border border-gray-700" data-testid={`war-board-month-${m.month_str_est}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold text-lg text-white">{m.month_str_est}</span>
                            <span className="px-2 py-0.5 rounded bg-green-700/50 text-green-300 text-sm font-bold">{m.units_sold} units</span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                            {Object.entries(m.daily_totals || {}).map(([k, v]) => (
                              <div key={k} className="bg-gray-900/60 rounded p-2">
                                <div className="text-gray-500 capitalize">{k}</div>
                                <div className="text-white font-bold text-base">{v}</div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-2 text-xs text-gray-400">
                            {m.days_tracked} day{m.days_tracked === 1 ? '' : 's'} tracked
                            {m.top_brand ? ` · Top brand: ${m.top_brand}` : ''}
                            {Array.isArray(m.deals) && m.deals.length ? ` · ${m.deals.length} deals` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-bold text-cyan-300 uppercase tracking-wider mb-3">Recent Daily Snapshots</h4>
                  {history.daily.length === 0 ? (
                    <p className="text-gray-500 text-sm italic">No daily snapshots yet — once you log progress, it'll auto-archive each night at 11:59 PM EST.</p>
                  ) : (
                    <div className="space-y-2">
                      {history.daily.slice(0, 14).map((d) => (
                        <div key={d.id} className="bg-gray-800/40 rounded-lg p-3 border border-gray-700/50 text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-bold text-gray-200">{d.date_str_est}</span>
                            <span className="text-xs text-gray-500">Units: {d.units_at_end_of_day}</span>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-400">
                            {Object.entries(d.daily_progress || {}).map(([k, v]) => (
                              <span key={k} className="bg-gray-900/60 px-2 py-0.5 rounded">
                                <span className="capitalize">{k}</span> · <span className="text-white font-bold">{v}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-red-600">
            <div className="flex items-center gap-2 mb-4 text-red-400">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="text-xl font-bold">Reset Entire Month?</h3>
            </div>
            <p className="text-gray-300 mb-6">
              This will reset your unit count to 0 and restore the original deals. Your daily progress will also be cleared. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={resetMonth}
                className="flex-1 py-2 bg-red-600 hover:bg-red-500 rounded-lg font-bold transition-colors"
              >
                Yes, Reset Month
              </button>
            </div>
          </div>
        </div>
      )}

      {showGoalEditor && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-blue-500/50 shadow-2xl">
            <div className="flex items-center gap-2 mb-3 text-blue-300">
              <Target className="w-6 h-6" />
              <h3 className="text-xl font-bold">Set Monthly Goal</h3>
            </div>
            <p className="text-gray-300 text-sm mb-4">
              How many units are you targeting for <span className="font-bold text-white">{TARGET_MONTH_NAME} {TARGET_YEAR}</span>? Change this at the start of each month.
            </p>
            <input
              type="number"
              min="1"
              max="9999"
              value={goalDraft}
              onChange={(e) => setGoalDraft(e.target.value)}
              className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white text-2xl font-black text-center focus:outline-none focus:border-blue-500"
              data-testid="monthly-goal-input"
              autoFocus
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowGoalEditor(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
                data-testid="cancel-goal-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const n = parseInt(goalDraft, 10);
                  if (Number.isFinite(n) && n > 0) {
                    setMonthlyGoal(n);
                    setShowGoalEditor(false);
                  }
                }}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg font-bold transition-colors"
                data-testid="save-goal-btn"
              >
                Save Goal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

// Score Card Component
const ScoreCard = ({ label, value, icon, color, bgColor, borderColor, pulse, small }) => (
  <div className={`bg-gradient-to-br ${bgColor} rounded-xl p-4 border ${borderColor} shadow-lg ${pulse ? 'animate-pulse' : ''}`}>
    <div className={`${color} mb-2`}>{icon}</div>
    <p className="text-xs font-bold text-gray-400 tracking-wider mb-1">{label}</p>
    <p className={`${small ? 'text-2xl' : 'text-3xl md:text-4xl'} font-black ${color}`}>{value}</p>
  </div>
);

// CRM Reconciliation Diff Modal — shown after AI extracts MTD numbers from
// the dealer's CRM screenshot. Lets the dealer review each field, opt out,
// or override the proposed value before it overwrites their war-board MTD totals.
const ReconcileDiffModal = ({ preview, monthlyProgress, submitting, onCancel, onApply }) => {
  const FIELD_LABELS = {
    calls: '📞 Calls', texts: '💬 Texts', followups: '🔄 Follow-Ups',
    leads: '🎯 New Leads', appointments: '📅 Appointments Set',
    shown: '👋 Appointments Shown', sold: '🏆 Sold',
    communications: '📡 Communications (combined)',
  };
  const proposed = preview.proposed || {};
  const fields = Object.keys(proposed);
  // Local state — each field can be toggled on/off and its target value edited
  const [keep, setKeep] = useState(() => {
    const m = {};
    for (const f of fields) m[f] = true;
    return m;
  });
  const [values, setValues] = useState(() => ({ ...proposed }));

  const handleApply = () => {
    const final = {};
    for (const f of fields) {
      if (keep[f] && Number.isFinite(Number(values[f]))) final[f] = Math.max(0, Math.round(Number(values[f])));
    }
    onApply(final);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-2 sm:p-4" data-testid="reconcile-modal">
      <div className="bg-gray-900 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" /> Reconcile MTD totals from CRM
            </h3>
            <p className="text-xs text-gray-400 mt-1">
              Each value below will <b className="text-amber-300">replace</b> your current month-to-date total. CRM is the source of truth — totals won't be added on top.
              <br />Reviewed {preview.image_count || 1} screenshot{(preview.image_count || 1) === 1 ? '' : 's'}.
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-white" data-testid="reconcile-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2 my-4">
          {fields.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No metrics extracted.</p>
          ) : fields.map((f) => {
            const prev = monthlyProgress[f] || 0;
            const next = Number(values[f]);
            const delta = Number.isFinite(next) ? next - prev : 0;
            return (
              <div key={f} className={`rounded-xl p-3 border ${keep[f] ? 'bg-gray-800/70 border-gray-700' : 'bg-gray-900/50 border-gray-800 opacity-60'}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-2 text-sm text-white cursor-pointer min-w-0">
                    <input
                      type="checkbox"
                      checked={!!keep[f]}
                      onChange={(e) => setKeep((m) => ({ ...m, [f]: e.target.checked }))}
                      className="accent-amber-500 w-4 h-4"
                      data-testid={`reconcile-keep-${f}`}
                    />
                    <span className="font-semibold truncate">{FIELD_LABELS[f] || f}</span>
                  </label>
                  <div className="text-xs text-gray-400 shrink-0">
                    Current MTD: <span className="text-white font-bold">{prev}</span>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500">New MTD value:</span>
                  <input
                    type="number"
                    min="0"
                    value={Number.isFinite(next) ? next : ''}
                    onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))}
                    disabled={!keep[f]}
                    className="bg-gray-950 border border-gray-700 rounded-md px-2 py-1 w-24 text-white font-mono text-sm focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:opacity-40"
                    data-testid={`reconcile-value-${f}`}
                  />
                  <span className={`text-xs font-bold ${delta === 0 ? 'text-gray-500' : delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {keep[f] && delta !== 0 ? `${delta > 0 ? '+' : ''}${delta}` : ''}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2 border-t border-gray-800">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium text-sm"
            data-testid="reconcile-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={submitting || Object.values(keep).every((k) => !k)}
            className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold text-sm shadow disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="reconcile-apply"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Replace MTD totals
          </button>
        </div>
      </div>
    </div>
  );
};


// Countdown Unit Component
const CountdownUnit = ({ value, label, highlight }) => (
  <div className="text-center">
    <div className={`text-3xl md:text-4xl font-black tabular-nums ${highlight ? 'text-red-400' : 'text-white'}`}>
      {String(value || 0).padStart(2, '0')}
    </div>
    <div className="text-[10px] font-bold text-gray-500 tracking-wider">{label}</div>
  </div>
);

export default SalesWarBoard;
